/**
 * FunnelAssistPanel - Canonical recommendation client for MA-ARCH-174
 * 
 * Single canonical recommendation layer backed by funnel_recommendations API.
 * Every actionable recommendation corresponds to a persisted backend record.
 * 
 * State machine: pending -> applied | rejected
 * Classification: execution_improvement | strategy_change
 * 
 * Displays structured diffs, source drift warnings, strategy change warnings.
 * Preserves richness: severity, evidence, impact, confidence.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Sparkles,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  Check,
  X,
  Loader2,
  RefreshCw,
  Play,
  Target,
  DollarSign,
  Users,
  ArrowRight,
  Info,
  Zap,
  Calendar,
  BarChart3,
  Edit3,
  Plus,
  Minus,
  GitBranch,
  CheckCircle2,
  Clock,
  ShieldAlert,
  ArrowUpRight,
  Settings,
  Route,
  FileText,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';


// =============================================================================
// Configuration
// =============================================================================

const SEVERITY_CONFIG = {
  high: { color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30', label: 'High' },
  medium: { color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30', label: 'Medium' },
  low: { color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30', label: 'Low' },
};

const CONFIDENCE_LABEL = (val) => {
  if (val >= 0.8) return { color: 'text-green-400', label: 'High' };
  if (val >= 0.5) return { color: 'text-amber-400', label: 'Medium' };
  return { color: 'text-slate-400', label: 'Low' };
};

const STATUS_CONFIG = {
  pending: { color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30', label: 'Pending', icon: Clock },
  approved: { color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/30', label: 'Approved', icon: Check },
  applied: { color: 'text-indigo-400', bg: 'bg-indigo-500/20', border: 'border-indigo-500/30', label: 'Applied', icon: CheckCircle2 },
  rejected: { color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30', label: 'Rejected', icon: X },
};

const TYPE_CONFIG = {
  execution_improvement: {
    label: 'Execution',
    color: 'text-blue-400',
    bg: 'bg-blue-500/20',
    border: 'border-blue-500/30',
    icon: Settings,
    description: 'Creates a new draft funnel version',
  },
  strategy_change: {
    label: 'Strategy',
    color: 'text-purple-400',
    bg: 'bg-purple-500/20',
    border: 'border-purple-500/30',
    icon: GitBranch,
    description: 'Creates a new Profit Path branch',
  },
};

const SCOPE_ICONS = {
  copy: Edit3,
  structure: Route,
  qualification: Target,
  booking: Calendar,
  consent: ShieldAlert,
  offer: DollarSign,
  message: FileText,
  routing: ArrowUpRight,
  mixed: Settings,
};

const IMPACT_ARROWS = {
  increase: { icon: TrendingUp, color: 'text-emerald-400' },
  decrease: { icon: TrendingDown, color: 'text-red-400' },
  unknown: { icon: BarChart3, color: 'text-slate-400' },
};


// =============================================================================
// Sub-Components
// =============================================================================

function ImpactDisplay({ impact }) {
  if (!impact) return null;
  const metrics = [
    { key: 'conversion_rate', label: 'Conversion Rate' },
    { key: 'lead_quality', label: 'Lead Quality' },
    { key: 'show_rate', label: 'Show Rate' },
    { key: 'close_rate', label: 'Close Rate' },
  ];

  const displayed = metrics.filter(m => impact[m.key] && impact[m.key] !== 'unknown');
  if (!displayed.length) return null;

  return (
    <div className="space-y-1" data-testid="impact-display">
      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Expected Impact</p>
      <div className="grid grid-cols-2 gap-1.5">
        {displayed.map(({ key, label }) => {
          const dir = impact[key];
          const cfg = IMPACT_ARROWS[dir] || IMPACT_ARROWS.unknown;
          const Icon = cfg.icon;
          return (
            <div key={key} className="flex items-center gap-1.5 p-1.5 rounded bg-white/[0.02] border border-white/[0.05] text-xs">
              <Icon className={cn("h-3 w-3", cfg.color)} />
              <span className="text-slate-300">{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function StructuredDiffDisplay({ preview, recType }) {
  if (!preview) return null;

  const execDiff = preview.execution_diff || {};
  const stratDiff = preview.strategy_diff || [];

  const hasExecChanges = (
    (execDiff.nodes_added?.length || 0) +
    (execDiff.nodes_removed?.length || 0) +
    (execDiff.nodes_modified?.length || 0) +
    (execDiff.edges_added?.length || 0) +
    (execDiff.edges_removed?.length || 0) +
    (execDiff.edges_modified?.length || 0) +
    (execDiff.settings_changed?.length || 0)
  ) > 0;

  const hasStratChanges = stratDiff.length > 0;

  if (!hasExecChanges && !hasStratChanges) {
    return (
      <div className="text-xs text-slate-500 italic p-2">No structural changes detected in preview.</div>
    );
  }

  return (
    <div className="space-y-3" data-testid="structured-diff">
      {/* Strategy deltas (shown first for strategy_change) */}
      {hasStratChanges && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-purple-400 uppercase tracking-wide flex items-center gap-1">
            <GitBranch className="h-3 w-3" /> Strategy Deltas
          </p>
          {stratDiff.map((d, i) => (
            <div key={i} className="p-2 rounded bg-purple-500/10 border border-purple-500/20 text-xs">
              <span className="text-slate-400">{d.field?.replace(/_/g, ' ')}: </span>
              <span className="text-red-400 line-through mr-1">{d.old_value || '(empty)'}</span>
              <ArrowRight className="h-3 w-3 inline text-slate-500 mx-0.5" />
              <span className="text-green-400 ml-1">{d.new_value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Execution deltas */}
      {hasExecChanges && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-blue-400 uppercase tracking-wide flex items-center gap-1">
            <Settings className="h-3 w-3" /> Execution Deltas
          </p>

          {/* Nodes added */}
          {execDiff.nodes_added?.map((n, i) => (
            <DiffRow key={`na-${i}`} action="add" icon={Plus} label={`Add node: ${n.label || n.node_type || n.node_id}`} />
          ))}

          {/* Nodes removed */}
          {execDiff.nodes_removed?.map((n, i) => (
            <DiffRow key={`nr-${i}`} action="remove" icon={Minus} label={`Remove node: ${n.label || n.node_id}`} />
          ))}

          {/* Nodes modified */}
          {execDiff.nodes_modified?.map((n, i) => (
            <DiffRow key={`nm-${i}`} action="modify" icon={Edit3}
              label={`Modify ${n.node_id}: ${n.field}`}
              detail={
                <span className="text-xs">
                  <span className="text-red-400 line-through">{truncate(String(n.old_value ?? '(empty)'), 60)}</span>
                  {' -> '}
                  <span className="text-green-400">{truncate(String(n.new_value ?? ''), 60)}</span>
                </span>
              }
            />
          ))}

          {/* Edges */}
          {execDiff.edges_added?.map((e, i) => (
            <DiffRow key={`ea-${i}`} action="add" icon={ArrowUpRight} label={`Add edge: ${e.edge_id}`} />
          ))}
          {execDiff.edges_removed?.map((e, i) => (
            <DiffRow key={`er-${i}`} action="remove" icon={Minus} label={`Remove edge: ${e.edge_id}`} />
          ))}
          {execDiff.edges_modified?.map((e, i) => (
            <DiffRow key={`em-${i}`} action="modify" icon={Edit3} label={`Modify edge ${e.edge_id}: ${e.field}`} />
          ))}

          {/* Settings */}
          {execDiff.settings_changed?.map((s, i) => (
            <DiffRow key={`sc-${i}`} action="modify" icon={Settings}
              label={`Setting: ${s.path}`}
              detail={
                <span className="text-xs">
                  <span className="text-red-400">{truncate(String(s.old_value ?? '(empty)'), 40)}</span>
                  {' -> '}
                  <span className="text-green-400">{truncate(String(s.new_value ?? ''), 40)}</span>
                </span>
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DiffRow({ action, icon: Icon, label, detail }) {
  const colors = {
    add: { bg: 'bg-green-500/10', border: 'border-green-500/20', text: 'text-green-400' },
    remove: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400' },
    modify: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400' },
  };
  const c = colors[action] || colors.modify;

  return (
    <div className={cn("p-2 rounded text-xs", c.bg, "border", c.border)}>
      <div className="flex items-center gap-1.5">
        <Icon className={cn("h-3 w-3 shrink-0", c.text)} />
        <span className="text-slate-200">{label}</span>
      </div>
      {detail && <div className="mt-1 pl-4.5">{detail}</div>}
    </div>
  );
}


function ScopeBadges({ scopes }) {
  if (!scopes?.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {scopes.map(s => {
        const Icon = SCOPE_ICONS[s] || Settings;
        return (
          <Badge key={s} variant="outline" className="text-[10px] text-slate-400 border-white/10 gap-1">
            <Icon className="h-2.5 w-2.5" />
            {s}
          </Badge>
        );
      })}
    </div>
  );
}


// =============================================================================
// Performance Outcome Indicator (MA-ARCH-176)
// =============================================================================

const IMPACT_CONFIG = {
  positive: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', label: 'Positive Impact' },
  negative: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', label: 'Negative Impact' },
  neutral: { color: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/20', label: 'Neutral' },
};

const STATE_LABELS = {
  low_data: { label: 'Low Data', color: 'text-slate-400', desc: 'Not enough data for reliable analysis' },
  early_signal: { label: 'Early Signal', color: 'text-amber-400', desc: 'Preliminary data — may change' },
  significant: { label: 'Stable', color: 'text-green-400', desc: 'Sufficient data for reliable analysis' },
};

function PerformanceIndicator({ outcome }) {
  if (!outcome || outcome.status === 'not_applied') return null;

  const state = STATE_LABELS[outcome.analytical_state] || STATE_LABELS.low_data;
  const impact = IMPACT_CONFIG[outcome.impact] || IMPACT_CONFIG.neutral;
  const metrics = outcome.new_version_metrics || {};
  const deltas = outcome.deltas || {};

  return (
    <div className={cn("mt-2 p-2.5 rounded-lg border", impact.bg, impact.border)} data-testid="performance-indicator">
      <div className="flex items-center justify-between mb-1.5">
        <span className={cn("text-xs font-medium flex items-center gap-1", impact.color)}>
          {outcome.impact === 'positive' && <TrendingUp className="h-3 w-3" />}
          {outcome.impact === 'negative' && <TrendingDown className="h-3 w-3" />}
          {outcome.impact === 'neutral' && <BarChart3 className="h-3 w-3" />}
          {impact.label}
        </span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className={cn("text-[10px]", state.color, "border-white/10 cursor-help")}>
                {state.label}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="left" className="bg-slate-800 text-xs max-w-[200px]">
              {state.desc}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="grid grid-cols-3 gap-1.5 text-xs">
        <div className="text-center p-1 rounded bg-white/[0.03]">
          <div className="text-slate-400">Leads</div>
          <div className="text-slate-200 font-medium">{metrics.leads ?? 0}</div>
        </div>
        <div className="text-center p-1 rounded bg-white/[0.03]">
          <div className="text-slate-400">Bookings</div>
          <div className="text-slate-200 font-medium">{metrics.appointments ?? 0}</div>
        </div>
        <div className="text-center p-1 rounded bg-white/[0.03]">
          <div className="text-slate-400">Revenue</div>
          <div className="text-slate-200 font-medium">${(metrics.revenue ?? 0).toLocaleString()}</div>
        </div>
      </div>

      {/* Show deltas only if we have significant data */}
      {Object.keys(deltas).length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {deltas.booking_rate != null && (
            <DeltaBadge label="Booking" value={deltas.booking_rate} />
          )}
          {deltas.revenue_per_lead != null && (
            <DeltaBadge label="Rev/Lead" value={deltas.revenue_per_lead} />
          )}
          {deltas.close_rate != null && (
            <DeltaBadge label="Close" value={deltas.close_rate} />
          )}
        </div>
      )}
    </div>
  );
}

function DeltaBadge({ label, value }) {
  const isPos = value > 0;
  const isNeg = value < 0;
  return (
    <span className={cn(
      "text-[10px] px-1.5 py-0.5 rounded",
      isPos && "bg-emerald-500/10 text-emerald-400",
      isNeg && "bg-red-500/10 text-red-400",
      !isPos && !isNeg && "bg-slate-500/10 text-slate-400",
    )}>
      {label}: {isPos ? '+' : ''}{(value * 100).toFixed(1)}%
    </span>
  );
}


// =============================================================================
// Recommendation Card
// =============================================================================

function RecommendationCard({ rec, outcome, onApply, onReject, onViewDetail, isProcessing }) {
  const [expanded, setExpanded] = useState(false);

  const statusCfg = STATUS_CONFIG[rec.status] || STATUS_CONFIG.pending;
  const typeCfg = TYPE_CONFIG[rec.recommendation_type] || TYPE_CONFIG.execution_improvement;
  const TypeIcon = typeCfg.icon;
  const confCfg = CONFIDENCE_LABEL(rec.confidence || 0.5);

  const isPending = rec.status === 'pending';
  const isApplied = rec.status === 'applied';
  const isRejected = rec.status === 'rejected';
  const hasDrift = rec.preview?.source_drift_detected;

  return (
    <Card
      className={cn(
        "glass border-white/10 transition-all",
        expanded && "ring-1 ring-indigo-500/30",
        hasDrift && isPending && "ring-1 ring-amber-500/30",
      )}
      data-testid={`recommendation-card-${rec.id}`}
    >
      <CardContent className="p-4">
        {/* Header row */}
        <div className="flex items-start gap-3">
          {/* Type icon */}
          <div className={cn("p-2 rounded-lg shrink-0", typeCfg.bg)}>
            <TypeIcon className={cn("h-5 w-5", typeCfg.color)} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-medium text-slate-100 text-sm">
                {rec.summary || rec.reason?.substring(0, 80) || 'Recommendation'}
              </h4>
              <Badge variant="outline" className={cn("text-[10px]", typeCfg.color, typeCfg.border)}>
                {typeCfg.label}
              </Badge>
              <Badge variant="outline" className={cn("text-[10px]", statusCfg.color, statusCfg.border)}>
                {statusCfg.label}
              </Badge>
            </div>

            <p className="text-sm text-slate-400 mt-1 line-clamp-2">{rec.reason}</p>

            {/* Meta row */}
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
              {rec.source_type === 'funnelassist_ai' && (
                <span className="flex items-center gap-1 text-indigo-400">
                  <Sparkles className="h-3 w-3" />
                  FunnelAssist AI
                </span>
              )}
              {rec.source_type === 'manual' && (
                <span className="flex items-center gap-1 text-slate-400">
                  <Users className="h-3 w-3" />
                  Manual
                </span>
              )}
              <span className={cn("flex items-center gap-1", confCfg.color)}>
                <Zap className="h-3 w-3" />
                {confCfg.label} ({(rec.confidence * 100).toFixed(0)}%)
              </span>
              <ScopeBadges scopes={rec.change_scope} />
              {rec.source_funnel_version_id && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1 text-slate-500 cursor-help">
                        <FileText className="h-3 w-3" />
                        v:{rec.source_funnel_version_id.slice(-6)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="bg-slate-800 text-xs">
                      Source: {rec.source_funnel_version_id}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {rec.created_at && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDistanceToNow(new Date(rec.created_at), { addSuffix: true })}
                </span>
              )}
            </div>

            {/* Source drift warning */}
            {hasDrift && isPending && (
              <Alert className="mt-2 bg-amber-500/10 border-amber-500/20 py-1.5 px-2.5">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
                <AlertDescription className="text-amber-200 text-xs">
                  Source drift: the active funnel version has changed since this recommendation was generated.
                </AlertDescription>
              </Alert>
            )}

            {/* Strategy change warning */}
            {rec.recommendation_type === 'strategy_change' && isPending && (
              <Alert className="mt-2 bg-purple-500/10 border-purple-500/20 py-1.5 px-2.5">
                <GitBranch className="h-3.5 w-3.5 text-purple-400" />
                <AlertDescription className="text-purple-200 text-xs">
                  Strategy change: applying will create a new Profit Path branch.
                </AlertDescription>
              </Alert>
            )}

            {/* Applied result */}
            {isApplied && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                {rec.applied_funnel_version_id && (
                  <Badge variant="outline" className="text-[10px] text-indigo-400 border-indigo-500/30">
                    Version: {rec.applied_funnel_version_id}
                  </Badge>
                )}
                {rec.applied_profit_path_id && (
                  <Badge variant="outline" className="text-[10px] text-purple-400 border-purple-500/30">
                    New PP: {rec.applied_profit_path_id}
                  </Badge>
                )}
              </div>
            )}

            {/* Performance outcome (MA-ARCH-176) */}
            {isApplied && outcome && (
              <PerformanceIndicator outcome={outcome} />
            )}

            {/* Error metadata */}
            {rec.error_metadata && (
              <Alert className="mt-2 bg-red-500/10 border-red-500/20 py-1.5 px-2.5">
                <AlertTriangle className="h-3.5 w-3.5 text-red-400" />
                <AlertDescription className="text-red-200 text-xs">
                  Previous apply failed: {rec.error_metadata.error}
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Expand */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (!expanded) onViewDetail(rec.id);
              setExpanded(!expanded);
            }}
            className="h-8 w-8 p-0 shrink-0"
            data-testid={`expand-rec-${rec.id}`}
          >
            <ChevronRight className={cn("h-4 w-4 transition-transform", expanded && "rotate-90")} />
          </Button>
        </div>

        {/* Expanded detail */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-white/10 space-y-4">
            <ImpactDisplay impact={rec.expected_impact} />
            <StructuredDiffDisplay preview={rec.preview} recType={rec.recommendation_type} />

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2">
              {isPending && (
                <>
                  <Button
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => onApply(rec)}
                    disabled={isProcessing}
                    data-testid={`apply-rec-btn-${rec.id}`}
                  >
                    {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />}
                    Apply
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    onClick={() => onReject(rec.id)}
                    disabled={isProcessing}
                    data-testid={`reject-rec-btn-${rec.id}`}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                </>
              )}
              {isApplied && (
                <Badge className="bg-indigo-500/20 text-indigo-300 text-xs">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Applied {rec.applied_at ? formatDistanceToNow(new Date(rec.applied_at), { addSuffix: true }) : ''}
                </Badge>
              )}
              {isRejected && (
                <Badge className="bg-red-500/20 text-red-300 text-xs">
                  <X className="h-3 w-3 mr-1" />
                  Rejected {rec.rejected_at ? formatDistanceToNow(new Date(rec.rejected_at), { addSuffix: true }) : ''}
                </Badge>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// =============================================================================
// Apply Confirmation Modal
// =============================================================================

function ApplyConfirmationModal({ rec, open, onClose, onConfirm, isApplying }) {
  if (!rec) return null;

  const typeCfg = TYPE_CONFIG[rec.recommendation_type] || TYPE_CONFIG.execution_improvement;
  const TypeIcon = typeCfg.icon;
  const hasDrift = rec.preview?.source_drift_detected;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-slate-900 border-white/10" data-testid="apply-confirmation-modal">
        <DialogHeader>
          <DialogTitle className="text-slate-100 flex items-center gap-2">
            <TypeIcon className={cn("h-5 w-5", typeCfg.color)} />
            Apply {typeCfg.label} Recommendation
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {rec.recommendation_type === 'strategy_change'
              ? 'This will create a new Profit Path branch with the strategy changes below.'
              : 'This will create a new draft funnel version with the changes below.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto">
          {/* Summary */}
          <div className="p-3 rounded bg-white/[0.02] border border-white/[0.05]">
            <p className="text-sm font-medium text-slate-100">{rec.summary || 'Recommendation'}</p>
            <p className="text-xs text-slate-400 mt-1">{rec.reason}</p>
          </div>

          {/* Structured diff preview */}
          <StructuredDiffDisplay preview={rec.preview} recType={rec.recommendation_type} />

          {/* Drift warning */}
          {hasDrift && (
            <Alert className="bg-amber-500/10 border-amber-500/20">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              <AlertDescription className="text-amber-200 text-sm">
                Source drift detected. The active version has changed since this recommendation was created.
                Applying will use the original source context.
              </AlertDescription>
            </Alert>
          )}

          {/* Strategy warning */}
          {rec.recommendation_type === 'strategy_change' && (
            <Alert className="bg-purple-500/10 border-purple-500/20">
              <GitBranch className="h-4 w-4 text-purple-400" />
              <AlertDescription className="text-purple-200 text-sm">
                This will create a new Profit Path with modified strategy fields.
                The original Profit Path will not be changed.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} disabled={isApplying} data-testid="cancel-apply-btn">
            Cancel
          </Button>
          <Button
            className={cn(
              rec.recommendation_type === 'strategy_change'
                ? "bg-purple-600 hover:bg-purple-700"
                : "bg-indigo-600 hover:bg-indigo-700"
            )}
            onClick={() => onConfirm(hasDrift)}
            disabled={isApplying}
            data-testid="confirm-apply-btn"
          >
            {isApplying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Applying...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {hasDrift ? 'Apply Despite Drift' : 'Apply Changes'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =============================================================================
// Main Component
// =============================================================================

export function FunnelAssistPanel({ profitPathId, profitPathName, funnelId, funnelName, className = '' }) {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [applyModal, setApplyModal] = useState({ open: false, rec: null });
  const [isApplying, setIsApplying] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null); // {success, summary, error}
  const [outcomes, setOutcomes] = useState({}); // rec_id -> outcome

  // Fetch recommendations from canonical API
  const fetchRecommendations = useCallback(async () => {
    if (!profitPathId) return;
    setLoading(true);
    try {
      const response = await api.get(`/funnel-recommendations/profit-path/${profitPathId}`);
      const recs = response.data?.recommendations || [];
      setRecommendations(recs);

      // Fetch performance outcomes for applied recommendations
      const appliedIds = recs.filter(r => r.status === 'applied').map(r => r.id);
      if (appliedIds.length > 0) {
        try {
          const perfResp = await api.get(`/recommendation-analytics/performance?profit_path_id=${profitPathId}`);
          const outcomeMap = {};
          for (const o of (perfResp.data?.outcomes || [])) {
            outcomeMap[o.recommendation_id] = o;
          }
          setOutcomes(outcomeMap);
        } catch {
          // Non-critical - analytics may not be available
        }
      }
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
      if (error?.response?.status !== 404) {
        toast({
          title: 'Failed to load recommendations',
          description: error?.response?.data?.detail || 'Please try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [profitPathId]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  // Fetch detail (with preview diff) for a specific recommendation
  const fetchDetail = async (recId) => {
    try {
      const response = await api.get(`/funnel-recommendations/${recId}`);
      const detail = response.data;
      setRecommendations(prev =>
        prev.map(r => r.id === recId ? { ...r, preview: detail.preview, recommendation_payload: detail.recommendation_payload } : r)
      );
    } catch (error) {
      console.error('Failed to fetch recommendation detail:', error);
    }
  };

  // Apply recommendation
  const handleApplyClick = (rec) => {
    // Fetch detail first to get fresh preview
    fetchDetail(rec.id).then(() => {
      setApplyModal({ open: true, rec: recommendations.find(r => r.id === rec.id) || rec });
    });
  };

  // Update modal rec when recommendations update
  useEffect(() => {
    if (applyModal.open && applyModal.rec) {
      const fresh = recommendations.find(r => r.id === applyModal.rec.id);
      if (fresh && fresh.preview) {
        setApplyModal(prev => ({ ...prev, rec: fresh }));
      }
    }
  }, [recommendations]);

  const handleApplyConfirm = async (forceDrift = false) => {
    const rec = applyModal.rec;
    if (!rec) return;

    setIsApplying(true);
    try {
      const params = forceDrift ? '?force_despite_drift=true' : '';
      await api.post(`/funnel-recommendations/${rec.id}/apply${params}`);
      toast({
        title: rec.recommendation_type === 'strategy_change' ? 'New Profit Path created' : 'Draft version created',
        description: rec.recommendation_type === 'strategy_change'
          ? 'A new Profit Path branch has been created with the strategy changes.'
          : 'A new draft funnel version has been created. Publish it when ready.',
      });
      setApplyModal({ open: false, rec: null });
      fetchRecommendations();
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Please try again.';
      toast({
        title: 'Apply failed',
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setIsApplying(false);
    }
  };

  // Reject recommendation
  const handleReject = async (recId) => {
    setProcessing(true);
    try {
      await api.post(`/funnel-recommendations/${recId}/reject`);
      toast({ title: 'Recommendation rejected' });
      fetchRecommendations();
    } catch (error) {
      toast({
        title: 'Action failed',
        description: error?.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  // Generate recommendations (MA-ARCH-175)
  const handleGenerate = async () => {
    if (!profitPathId || generating) return;
    setGenerating(true);
    setGenerateResult(null);
    try {
      const response = await api.post('/funnel-recommendations/generate', {
        profit_path_id: profitPathId,
      });
      const data = response.data;
      if (data.success) {
        setGenerateResult({
          success: true,
          summary: data.summary,
          count: data.generated_count,
          suppressed: data.duplicates_suppressed,
        });
        toast({
          title: `Generated ${data.generated_count} recommendation${data.generated_count !== 1 ? 's' : ''}`,
          description: data.duplicates_suppressed > 0
            ? `${data.duplicates_suppressed} duplicate(s) suppressed.`
            : 'Review and apply the recommendations below.',
        });
        fetchRecommendations();
      } else {
        setGenerateResult({ success: false, error: data.error });
        toast({
          title: 'Generation failed',
          description: data.error || 'Please try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.response?.data?.error || 'Please try again.';
      setGenerateResult({ success: false, error: detail });
      toast({
        title: 'Generation failed',
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  // Stats
  const pendingCount = recommendations.filter(r => r.status === 'pending').length;
  const appliedCount = recommendations.filter(r => r.status === 'applied').length;
  const rejectedCount = recommendations.filter(r => r.status === 'rejected').length;

  const displayName = profitPathName || funnelName || 'this Profit Path';

  return (
    <div className={cn("space-y-4", className)} data-testid="funnel-assist-panel">
      {/* Header */}
      <Card className="glass border-white/10">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-slate-100 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-indigo-400" />
                FunnelAssist Recommendations
              </CardTitle>
              <CardDescription className="text-slate-400 mt-1">
                Canonical recommendations for {displayName}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {profitPathId && (
                <Button
                  size="sm"
                  className={cn(
                    "bg-indigo-600 hover:bg-indigo-700 transition-all",
                    generating && "opacity-80"
                  )}
                  onClick={handleGenerate}
                  disabled={generating || !profitPathId}
                  data-testid="generate-recommendation-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-1.5" />
                      Generate Recommendation
                    </>
                  )}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={fetchRecommendations}
                disabled={loading}
                className="border-white/10"
                data-testid="refresh-recommendations-btn"
              >
                <RefreshCw className={cn("h-4 w-4 mr-1", loading && "animate-spin")} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Generate result feedback */}
          {generateResult && !generating && (
            <div className="mt-3">
              {generateResult.success ? (
                <Alert className="bg-green-500/10 border-green-500/20 py-1.5 px-2.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
                  <AlertDescription className="text-green-200 text-xs">
                    {generateResult.summary || `Generated ${generateResult.count} recommendation(s).`}
                    {generateResult.suppressed > 0 && ` (${generateResult.suppressed} duplicate(s) suppressed)`}
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="bg-red-500/10 border-red-500/20 py-1.5 px-2.5">
                  <AlertTriangle className="h-3.5 w-3.5 text-red-400" />
                  <AlertDescription className="text-red-200 text-xs">
                    {generateResult.error || 'Generation failed. Please try again.'}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Stats */}
          {recommendations.length > 0 && (
            <div className="flex items-center gap-4 mt-3 text-sm">
              <span className="flex items-center gap-1 text-amber-400" data-testid="pending-count">
                <Clock className="h-4 w-4" />
                {pendingCount} pending
              </span>
              <span className="flex items-center gap-1 text-indigo-400" data-testid="applied-count">
                <CheckCircle2 className="h-4 w-4" />
                {appliedCount} applied
              </span>
              <span className="flex items-center gap-1 text-red-400" data-testid="rejected-count">
                <X className="h-4 w-4" />
                {rejectedCount} rejected
              </span>
            </div>
          )}
        </CardHeader>
      </Card>

      {/* Loading */}
      {loading && (
        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />
              <span className="ml-2 text-slate-400 text-sm">Loading recommendations...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!loading && recommendations.length === 0 && (
        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex flex-col items-center justify-center h-48 text-center" data-testid="empty-recommendations">
              <div className="p-3 rounded-full bg-indigo-500/20 mb-4">
                <Sparkles className="h-8 w-8 text-indigo-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-100 mb-2">No recommendations yet</h3>
              <p className="text-sm text-slate-400 max-w-sm mb-4">
                {profitPathId
                  ? 'Click "Generate Recommendation" to analyze your funnel and get AI-powered improvement suggestions.'
                  : 'FunnelAssist recommendations will appear here when this funnel is linked to a Profit Path.'}
              </p>
              {profitPathId && (
                <Button
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleGenerate}
                  disabled={generating}
                  data-testid="generate-empty-state-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-1.5" />
                      Generate Recommendation
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendation list */}
      {!loading && recommendations.length > 0 && (
        <div className="space-y-3" data-testid="recommendations-list">
          {recommendations.map((rec) => (
            <RecommendationCard
              key={rec.id}
              rec={rec}
              outcome={outcomes[rec.id]}
              onApply={handleApplyClick}
              onReject={handleReject}
              onViewDetail={fetchDetail}
              isProcessing={processing}
            />
          ))}
        </div>
      )}

      {/* Apply modal */}
      <ApplyConfirmationModal
        rec={applyModal.rec}
        open={applyModal.open}
        onClose={(open) => setApplyModal({ ...applyModal, open })}
        onConfirm={handleApplyConfirm}
        isApplying={isApplying}
      />

      {/* Info */}
      <Alert className="bg-white/[0.02] border-white/10">
        <Info className="h-4 w-4 text-slate-400" />
        <AlertDescription className="text-slate-400 text-sm">
          All recommendations are canonical records. Execution improvements create draft funnel versions.
          Strategy changes create new Profit Path branches. Applied changes are tracked with full lineage.
        </AlertDescription>
      </Alert>
    </div>
  );
}


// =============================================================================
// Utility
// =============================================================================

function truncate(str, max) {
  if (!str) return '';
  return str.length > max ? str.substring(0, max) + '...' : str;
}


export default FunnelAssistPanel;
